'use client'
import { Box, Flex, Spoiler, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'

import RichText from '@/app/[locale]/price/_components/Richtext'
import { useStockContext } from '@/app/[locale]/price/stocks/[symbol]/_contexts/StockContext'
import ChevronDown from '@/assets/icons/chevron-down.svg'
import ChevronUp from '@/assets/icons/chevron-up.svg'
import { StocksContent } from '@/types/stock'
import { replaceStockPlaceholders } from '@/utils/stockPlaceholderReplacer'

import styles from './style.module.css'

interface AboutSectionProps {
  content: StocksContent
  stockName: string
}

export default function AboutSection({ content, stockName }: AboutSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const t = useTranslations('about')
  const { stock } = useStockContext()

  // Create custom converter for text nodes to replace stock placeholders
  const customConverters = useMemo(
    () =>
      ({ defaultConverters }: { defaultConverters: any }) => {
        const originalTextConverter = defaultConverters.text

        return {
          ...defaultConverters,
          text: (args: any) => {
            const { node } = args

            // Replace placeholders in text nodes before default processing
            if (node?.text && typeof node.text === 'string') {
              const replacedText = replaceStockPlaceholders(node.text, stock)

              // Create a new node with replaced text
              const modifiedNode = { ...node, text: replacedText }

              // Call the original converter with modified node
              return originalTextConverter({ ...args, node: modifiedNode })
            }

            // No placeholder replacement needed, use original converter
            return originalTextConverter(args)
          },
        }
      },
    [stock],
  )

  return (
    <Box className={styles.aboutContainer}>
      <Text fz={18} fw={600} lh={'32px'} component="h2" mb={16} c="content-primary">
        {t('stockTitle', { stockName })}
      </Text>
      <Box pos="relative">
        <Spoiler
          maxHeight={140}
          transitionDuration={300}
          showLabel={
            <Flex gap={8} align="center">
              {t('description.showMore')}
              <ChevronDown width={16} height={16} fill="var(--common-primary)" />
            </Flex>
          }
          hideLabel={
            <Flex gap={8} align="center">
              {t('description.showLess')}
              <ChevronUp width={16} height={16} fill="var(--common-primary)" />
            </Flex>
          }
          expanded={isExpanded}
          onExpandedChange={setIsExpanded}
          classNames={{
            control: styles.spoilerControl,
          }}
        >
          <Box className={styles.content}>
            <RichText data={content.description} customConverters={customConverters} />
          </Box>
        </Spoiler>

        {!isExpanded && <Box className={styles.spoilerShadow} />}
      </Box>
    </Box>
  )
}
